import {
  ASSET_TYPE,
  AssetItem,
  AssetReport,
  CompareAssetCardData,
  InvestmentItem,
  InvestmentItemCompareData,
  LIABILITY_TYPE,
  LiabilityItem,
  REPORT_ITEM_TYPE,
  ReportItem,
  ReportItemCompareData,
} from '../type/AssetReport.type';
import { AssetStatusCardBarChartData } from '../component/AssetStatusCardBarChart';

export const getValidReportItemList = (reportItemList: ReportItem[]) => {
  return reportItemList.filter((reportItem) => !reportItem.excludeYn);
};

export const getAssetItemList = (reportItemList: ReportItem[]) => {
  return getValidReportItemList(reportItemList).filter(
    (reportItem) => reportItem.type === REPORT_ITEM_TYPE.ASSET,
  ) as AssetItem[];
};

export const getAssetSavingsItemList = (reportItemList: ReportItem[]) => {
  return getAssetItemList(reportItemList).filter((reportItem) => reportItem.assetType === ASSET_TYPE.SAVINGS);
};

export const getAssetInvestmentItemList = (reportItemList: ReportItem[]) => {
  return getAssetItemList(reportItemList).filter(
    (reportItem) => reportItem.assetType === ASSET_TYPE.INVESTMENT,
  ) as InvestmentItem[];
};

export const getAssetExpectToConsumeItemList = (reportItemList: ReportItem[]) => {
  return getAssetItemList(reportItemList).filter((reportItem) => reportItem.assetType === ASSET_TYPE.EXPECT_TO_CONSUME);
};

export const getAssetEtcItemList = (reportItemList: ReportItem[]) => {
  return getAssetItemList(reportItemList).filter((reportItem) => reportItem.assetType === ASSET_TYPE.ETC);
};

export const getLiabilityItemList = (reportItemList: ReportItem[]) => {
  return getValidReportItemList(reportItemList).filter(
    (reportItem) => reportItem.type === REPORT_ITEM_TYPE.LIABILITY,
  ) as LiabilityItem[];
};

export const getLiabilityLoanItemList = (reportItemList: ReportItem[]) => {
  return getLiabilityItemList(reportItemList).filter(
    (reportItem) => reportItem.liabilityType === LIABILITY_TYPE.LOAN,
  ) as LiabilityItem[];
};

export const getLiabilityExpectToConsumeItemList = (reportItemList: ReportItem[]) => {
  return getLiabilityItemList(reportItemList).filter(
    (reportItem) => reportItem.liabilityType === LIABILITY_TYPE.EXPECT_TO_CONSUME,
  ) as LiabilityItem[];
};

export const getLiabilityEtcItemList = (reportItemList: ReportItem[]) => {
  return getLiabilityItemList(reportItemList).filter(
    (reportItem) => reportItem.liabilityType === LIABILITY_TYPE.ETC,
  ) as LiabilityItem[];
};

const getTotalAmount = (reportItemList: ReportItem[]) => {
  return reportItemList.reduce((sum, item) => sum + item.amount, 0);
};

export const getAssetTotalAmount = (reportItemList: ReportItem[]) => {
  return getTotalAmount(getAssetItemList(reportItemList));
};

export const getAssetSavingsTotalAmount = (reportItemList: ReportItem[]) => {
  return getTotalAmount(getAssetSavingsItemList(reportItemList));
};

export const getAssetInvestmentTotalAmount = (reportItemList: ReportItem[]) => {
  return getTotalAmount(getAssetInvestmentItemList(reportItemList));
};

export const getAssetExpectToConsumeTotalAmount = (reportItemList: ReportItem[]) => {
  return getTotalAmount(getAssetExpectToConsumeItemList(reportItemList));
};
export const getAssetEtcTotalAmount = (reportItemList: ReportItem[]) => {
  return getTotalAmount(getAssetEtcItemList(reportItemList));
};

export const getLiabilityTotalAmount = (reportItemList: ReportItem[]) => {
  return getTotalAmount(getLiabilityItemList(reportItemList));
};

export const getLiabilityLoanTotalAmount = (reportItemList: ReportItem[]) => {
  return getTotalAmount(getLiabilityLoanItemList(reportItemList));
};

export const getLiabilityExpectTotalAmount = (reportItemList: ReportItem[]) => {
  return getTotalAmount(getLiabilityExpectToConsumeItemList(reportItemList));
};

export const getLiabilityEtcTotalAmount = (reportItemList: ReportItem[]) => {
  return getTotalAmount(getLiabilityEtcItemList(reportItemList));
};

export const getNetAssetTotalAmount = (reportItemList: ReportItem[]) => {
  debugger
  return getAssetTotalAmount(reportItemList) - getLiabilityTotalAmount(reportItemList);
};

export const getReportItemCompareDataList = (prevReportItemList: ReportItem[], reportItemList: ReportItem[]) => {
  if (!reportItemList || !prevReportItemList) return [];
  const reportItemCompareDataList: ReportItemCompareData[] = [];
  prevReportItemList.forEach((prevReportItem) => {
    const itemList = reportItemList.filter((item) => item.name === prevReportItem.name);
    const sortedItemList = itemList.sort(
      (a, b) => Math.abs(a.amount - prevReportItem.amount) - Math.abs(b.amount - prevReportItem.amount),
    );
    const item = sortedItemList.length > 0 ? sortedItemList[0] : undefined;
    if (item) {
      const itemIndex = reportItemList.findIndex(
        (savedItem) => savedItem.name === item.name && savedItem.amount === item.amount,
      );
      reportItemList = reportItemList.filter((reportItem, index) => index !== itemIndex);
    }
    reportItemCompareDataList.push({
      title: prevReportItem.name,
      prevItem: prevReportItem,
      item: item,
      changeAmount: (item ? item.amount : 0) - prevReportItem.amount,
    });
  });
  reportItemList.forEach((reportItem) => {
    reportItemCompareDataList.push({
      title: reportItem.name,
      prevItem: undefined,
      item: reportItem,
      changeAmount: reportItem.amount,
    });
  });
  return reportItemCompareDataList;
};

export const getInvestmentItemCompareDataList = (
  prevInvestmentItemList: InvestmentItem[],
  investmentItemList: InvestmentItem[],
) => {
  if (!investmentItemList || !prevInvestmentItemList) return [];
  const reportItemCompareDataList: InvestmentItemCompareData[] = getReportItemCompareDataList(
    prevInvestmentItemList,
    investmentItemList,
  ) as InvestmentItemCompareData[];
  const investmentItemCompareDataList: InvestmentItemCompareData[] = reportItemCompareDataList.map(
    (reportItemCompareData) => ({
      ...reportItemCompareData,
      changePrincipalAmount:
        (reportItemCompareData.item ? reportItemCompareData.item.principalAmount : 0) -
        (reportItemCompareData.prevItem
          ? reportItemCompareData.item
            ? reportItemCompareData.prevItem.principalAmount
            : reportItemCompareData.prevItem.amount
          : 0),
    }),
  );
  return investmentItemCompareDataList;
};

export const getEtcItemCompareDataList = (prevEtcItemList: ReportItem[], etcItemList: ReportItem[]) => {
  if (!etcItemList || !prevEtcItemList) return [];
  const reportItemCompareDataList: ReportItemCompareData[] = getReportItemCompareDataList(
    prevEtcItemList,
    etcItemList,
  ) as InvestmentItemCompareData[];
  const investmentItemCompareDataList: ReportItemCompareData[] = reportItemCompareDataList.map(
    (reportItemCompareData) => ({
      ...reportItemCompareData,
      changeAmount:
        reportItemCompareData.item?.type === REPORT_ITEM_TYPE.LIABILITY ||
        reportItemCompareData.prevItem?.type === REPORT_ITEM_TYPE.LIABILITY
          ? -reportItemCompareData.changeAmount
          : reportItemCompareData.changeAmount,
    }),
  );
  return investmentItemCompareDataList;
};

export const toAssetStatusCardBarChartData = (assetReport: AssetReport) =>
  ({
    assetTotalAmount: getAssetTotalAmount(assetReport.reportItemList),
    assetSavingsAmount: getAssetSavingsTotalAmount(assetReport.reportItemList),
    assetInvestmentAmount: getAssetInvestmentTotalAmount(assetReport.reportItemList),
    assetEtcAmount: getAssetEtcTotalAmount(assetReport.reportItemList),
    assetExpectedToConsumeAmount: getAssetExpectToConsumeTotalAmount(assetReport.reportItemList),
    liabilityTotalAmount: getLiabilityTotalAmount(assetReport.reportItemList),
    liabilityLoanAmount: getLiabilityLoanTotalAmount(assetReport.reportItemList),
    liabilityEtcAmount: getLiabilityEtcTotalAmount(assetReport.reportItemList),
    liabilityExpectedToConsumeAmount: getLiabilityExpectTotalAmount(assetReport.reportItemList),
  }) as AssetStatusCardBarChartData;

export const getCompareAssetCardData = (assetReportData: AssetReport, prevAssetReportData: AssetReport) => {
  const prevSavingItemList = getAssetSavingsItemList(prevAssetReportData.reportItemList);
  const savingItemList = getAssetSavingsItemList(assetReportData.reportItemList);
  const compareSavingItemList = getReportItemCompareDataList(prevSavingItemList, savingItemList).sort(
    (a, b) => b.changeAmount - a.changeAmount,
  );

  const prevInvestmentItemList = getAssetInvestmentItemList(prevAssetReportData.reportItemList);
  const investmentItemList = getAssetInvestmentItemList(assetReportData.reportItemList);
  const compareInvestmentItemList = getInvestmentItemCompareDataList(prevInvestmentItemList, investmentItemList).sort(
    (a, b) => b.changeAmount - a.changeAmount,
  );

  const prevEtcItemList = [
    ...getAssetEtcItemList(prevAssetReportData.reportItemList),
    ...getLiabilityLoanItemList(prevAssetReportData.reportItemList),
  ] as ReportItem[];
  const etcItemList = [
    ...getAssetEtcItemList(assetReportData.reportItemList),
    ...getLiabilityLoanItemList(assetReportData.reportItemList),
  ] as ReportItem[];
  const compareEtcItemList = getEtcItemCompareDataList(prevEtcItemList, etcItemList).sort(
    (a, b) => b.changeAmount - a.changeAmount,
  );

  const totalChangeAmount =
    getNetAssetTotalAmount(assetReportData.reportItemList) - getNetAssetTotalAmount(prevAssetReportData.reportItemList);
  const savingChangeAmount =
    getAssetSavingsTotalAmount(assetReportData.reportItemList) -
    getAssetSavingsTotalAmount(prevAssetReportData.reportItemList);
  const investmentChangeAmount =
    getAssetInvestmentTotalAmount(assetReportData.reportItemList) -
    getAssetInvestmentTotalAmount(prevAssetReportData.reportItemList);
  const investmentChangePrincipalAmount = compareInvestmentItemList
    .map((compareInvestmentItem) => compareInvestmentItem.changePrincipalAmount)
    .reduce((sum, changePrincipalAmount) => sum + changePrincipalAmount, 0);

  return {
    totalChangeAmount: totalChangeAmount,
    savingChangeAmount:
      getAssetSavingsTotalAmount(assetReportData.reportItemList) -
      getAssetSavingsTotalAmount(prevAssetReportData.reportItemList),
    investmentChangeAmount: investmentChangeAmount,
    investmentChangePrincipalAmount: investmentChangePrincipalAmount,
    investmentChangeProfitAmount: investmentChangeAmount - investmentChangePrincipalAmount,
    etcChangeAmount: totalChangeAmount - savingChangeAmount - investmentChangeAmount,
    compareSavingItemList: compareSavingItemList,
    compareInvestmentItemList: compareInvestmentItemList,
    compareEtcItemList: compareEtcItemList,
  } as CompareAssetCardData;
};
